// Gestionnaire de la forme récente de l'équipe Senior N2 dans Firestore
import { db } from '../firebase';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  addDoc, 
  deleteDoc,
  doc,
  serverTimestamp,
  where
} from 'firebase/firestore';
import axios from 'axios';
import logger from './logger';

const COLLECTION_NAME = 'forme_recente_n2';
const MAX_MATCHES = 5;
const API_BASE_URL = "https://api-dofa.fff.fr/api";
const CLUB_ID = 186901;

/**
 * Récupère les 5 derniers matchs depuis Firestore
 * @returns {Promise<Array>} Tableau des 5 derniers matchs
 */
export const getFormeRecenteFromFirestore = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('date', 'desc'),
      limit(MAX_MATCHES)
    );
    
    const snapshot = await getDocs(q);
    const matches = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return matches;
  } catch (error) {
    logger.error('Erreur récupération forme récente Firestore:', error);
    return [];
  }
};

/**
 * Ajoute un nouveau match dans Firestore
 * @param {Object} match - Données du match
 */
export const addMatchToFirestore = async (match) => {
  try {
    const matchData = {
      date: match.date,
      resultat: match.resultat, // 'V', 'N', 'D'
      adversaire: match.adversaire,
      score: match.score,
      domicile: match.domicile, // 'Domicile' ou 'Extérieur'
      competition: match.competition,
      timestamp: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), matchData);
    
    // Supprimer le 6ème match (le plus ancien) si on dépasse 5
    await cleanOldMatches();
    
    return docRef.id;
  } catch (error) {
    logger.error('Erreur ajout match Firestore:', error);
    throw error;
  }
};

/**
 * Supprime les matchs au-delà de MAX_MATCHES (garde seulement les 5 derniers)
 */
const cleanOldMatches = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.docs.length > MAX_MATCHES) {
      // Supprimer tous les matchs après le 5ème
      const toDelete = snapshot.docs.slice(MAX_MATCHES);
      
      for (const docToDelete of toDelete) {
        await deleteDoc(doc(db, COLLECTION_NAME, docToDelete.id));
      }
    }
  } catch (error) {
    logger.error('Erreur nettoyage anciens matchs:', error);
  }
};

/**
 * Synchronise les matchs de l'API FFF avec Firestore
 * Ajoute les nouveaux matchs détectés
 */
export const syncMatchsFromAPI = async () => {
  try {
    // Récupérer les matchs de l'API FFF
    const resultatsRes = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/resultat`);
    const allMatches = resultatsRes.data["hydra:member"] || resultatsRes.data || [];
    
    // Filtrer les matchs Senior N2 uniquement
    const matchsSeniorN2 = allMatches.filter(match => {
      const homeTeam = match?.home?.short_name_federation || match?.home?.nom || '';
      const awayTeam = match?.away?.short_name_federation || match?.away?.nom || '';
      
      const isEfafc = homeTeam.toLowerCase().includes('feignies') || 
                     homeTeam.toLowerCase().includes('aulnoye') ||
                     awayTeam.toLowerCase().includes('feignies') || 
                     awayTeam.toLowerCase().includes('aulnoye');
      
      if (!isEfafc) return false;
      
      // Exclure les catégories jeunes
      const competitionName = match?.competition?.name?.toLowerCase() || '';
      const isYouth = competitionName.includes('u17') || 
                     competitionName.includes('u18') || 
                     competitionName.includes('u19') ||
                     competitionName.includes('u15') ||
                     competitionName.includes('u16') ||
                     competitionName.includes('u13') ||
                     competitionName.includes('u14');
      
      const homeCategory = match?.home?.category_code;
      const awayCategory = match?.away?.category_code;
      const isSeniorByCode = (homeCategory === 'SEM' && match?.home?.number === 1) ||
                            (awayCategory === 'SEM' && match?.away?.number === 1);
      
      return !isYouth && (!homeCategory || !awayCategory || isSeniorByCode);
    });
    
    // Trier du plus récent au plus ancien
    const sortedMatches = matchsSeniorN2.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Récupérer les matchs existants dans Firestore
    const existingMatches = await getFormeRecenteFromFirestore();
    const existingDates = new Set(existingMatches.map(m => m.date));
    
    // Trouver les nouveaux matchs
    let addedCount = 0;
    for (const apiMatch of sortedMatches) {
      if (!existingDates.has(apiMatch.date)) {
        // Nouveau match détecté
        const homeTeam = apiMatch.home?.short_name_federation || apiMatch.home?.nom || '';
        const isHome = homeTeam.toLowerCase().includes('feignies') || 
                      homeTeam.toLowerCase().includes('aulnoye');
        
        const ourScore = isHome ? apiMatch.home_score : apiMatch.away_score;
        const theirScore = isHome ? apiMatch.away_score : apiMatch.home_score;
        
        let resultat = 'N';
        if (ourScore > theirScore) resultat = 'V';
        if (ourScore < theirScore) resultat = 'D';
        
        const matchData = {
          date: apiMatch.date,
          resultat,
          adversaire: isHome 
            ? (apiMatch.away?.short_name_federation || apiMatch.away?.nom || 'Adversaire')
            : (apiMatch.home?.short_name_federation || apiMatch.home?.nom || 'Adversaire'),
          score: `${ourScore}-${theirScore}`,
          domicile: isHome ? 'Domicile' : 'Extérieur',
          competition: apiMatch.competition?.name || 'N2'
        };
        
        await addMatchToFirestore(matchData);
        addedCount++;
        
        // Ne garder que les 5 premiers
        if (addedCount >= MAX_MATCHES) break;
      }
    }
    
    return addedCount;
  } catch (error) {
    logger.error('Erreur synchronisation API → Firestore:', error);
    return 0;
  }
};

/**
 * Initialise Firestore avec les matchs fournis manuellement
 * @param {Array} matches - Tableau de matchs à ajouter
 */
export const initializeFormeRecente = async (matches) => {
  try {
    // Vider la collection existante
    const q = query(collection(db, COLLECTION_NAME));
    const snapshot = await getDocs(q);
    for (const docToDelete of snapshot.docs) {
      await deleteDoc(doc(db, COLLECTION_NAME, docToDelete.id));
    }
    
    // Ajouter les nouveaux matchs
    for (const match of matches) {
      await addDoc(collection(db, COLLECTION_NAME), {
        ...match,
        timestamp: serverTimestamp()
      });
    }
    
    return true;
  } catch (error) {
    logger.error('Erreur initialisation forme récente:', error);
    return false;
  }
};

export default {
  getFormeRecenteFromFirestore,
  addMatchToFirestore,
  syncMatchsFromAPI,
  initializeFormeRecente
};

